import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { useUploadDealSignalFileMutation } from "../query/mutations/useUploadDealSignalFileMutation";
import type { DealSignalUploadInput } from "../types/model";
import signalRecommenderI18nKeys from "../utils/i18n";

import { useSignalRecommenderToasts } from "./useSignalRecommenderToasts";

type DealSignalEnrichment = {
    readonly isUploading: boolean;
    readonly startEnrichment: (input: DealSignalUploadInput) => void;
};

/**
 * Owns the upload. Progress after it is the files query's business — the job outlives this component,
 * so nothing here should be holding a timer for it.
 *
 * A rejected request is the one outcome the list cannot report: the history row is created by that very
 * request, so a submit that never lands leaves nothing behind to read. Hence the toast, which is also
 * why it is raised here rather than in the mutation — that layer stays free of UI.
 */
export const useDealSignalEnrichment = (): DealSignalEnrichment => {
    const { translate } = useTranslate();
    const { showError } = useSignalRecommenderToasts();

    const { mutate: uploadFile, isPending: isUploading } = useUploadDealSignalFileMutation();

    const startEnrichment = useCallback(
        (input: DealSignalUploadInput) => {
            uploadFile(input, {
                onError: () => showError(translate(signalRecommenderI18nKeys.toast.uploadError)),
            });
        },
        [uploadFile, showError, translate],
    );

    return { isUploading, startEnrichment };
};
